import React from 'react';
import { Pencil } from '@phosphor-icons/react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { Checkbox, Popover } from '../../../components/general';
import { SideIcon } from '../../../components/icons';

import { useProjectStore } from '../../../stores/project';
import { useUserStore } from '../../../stores/user';

import { Person } from '../../../types';
import { getFileImg } from '../../../utils';

import SampleAddOverlay from './sampleAddOverlay';

interface BaseProps {
  person: Person;
  onClick?: () => void;
  onDelete?: () => void;
  deletingFacesIds?: string[];
  isBig?: boolean;
  isShowDetails?: boolean;
  isSample?: boolean;
}

interface SelectionProps extends BaseProps {
  isSelectionMode: boolean;
  isSelected: boolean;
  onClickCheckbox: (id: string | null, isAddAll?: boolean) => void;
}

type Props = BaseProps | SelectionProps;

const FaceCircle: React.FC<Props> = (props) => {
  const {
    person,
    onClick,
    onDelete,
    deletingFacesIds,
    isBig,
    isShowDetails,
    isSample,
  } = props;

  const { adminMode } = useUserStore();
  const { currentProject } = useProjectStore();

  if (!currentProject || !person) return null;

  const face = person.face;

  const DeleteButton = (props: { className: string }) => {
    if (!onDelete) return null;

    return (
      <div className={props.className}>
        <SideIcon
          type="delete"
          deleteType="person"
          isProcessing={deletingFacesIds?.includes(face.elementId)}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        />
      </div>
    );
  };

  const PersonPhoto = (props: { className: string }) => {
    return (
      <img
        src={getFileImg(person.files?.[0])}
        className={props.className}
        alt={`${face.name} Icon`}
      />
    );
  };

  const DetailLine = (props: { label: string; value: string | undefined }) => {
    return (
      <div>
        <b>{props.label}</b>: {props.value ?? '—'}
      </div>
    );
  };

  // details view
  if (isShowDetails)
    return (
      <div className="group w-full relative">
        <DeleteButton className="w-full absolute" />

        <div
          className="group w-full flex items-center p-3 gap-2 sm:gap-4 rounded-md cursor-pointer hover:bg-grey-100"
          onClick={onClick}
        >
          <PersonPhoto className="w-24 h-24 rounded-full object-cover" />

          <div>
            <h5 className="flex items-center mb-2 gap-1">
              <div>{face.name}</div>{' '}
              <Pencil className="hidden group-hover:block hover:fill-[url('#icon-primary-gradient')]" />
            </h5>

            <div className="break-all">
              <DetailLine label="Email" value={face.email} />
              <DetailLine label="Phone Number" value={face.phoneNumber} />
              <DetailLine label="Instagram" value={face.instagram} />
              {currentProject.customFieldLabel && (
                <DetailLine
                  label={currentProject.customFieldLabel}
                  value={face.customFieldData}
                />
              )}
            </div>

            {person.shareLink && (
              <div className="italic">
                Watch generated video{' '}
                <Link
                  to={person.shareLink}
                  onClick={(e) => e.stopPropagation()}
                  target="_blank"
                >
                  here
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );

  const circleClass = 'w-full aspect-square rounded-full';

  // circle view
  return (
    <div
      className={classNames({
        'w-[var(--upload-el-width)]': isBig,
        'w-[calc((var(--page-width)_-_(1.5rem_*_2))_/_3)] sm:w-[calc((var(--page-width)_-_(1.5rem_*_6))_/_7)]':
          !isBig,
      })}
    >
      <button
        className={classNames(circleClass, {
          'group relative transition-transform duration-300': true,
          'cursor-pointer': !!onClick,
          'hover:scale-110': !!onClick && !isSample,
        })}
        onClick={onClick}
      >
        <DeleteButton className={classNames(circleClass, 'absolute')} />

        {isSample && (
          <SampleAddOverlay
            element={person}
            addClass={classNames(circleClass, 'text-[1.25rem]')}
          />
        )}

        <PersonPhoto className={classNames(circleClass, 'object-cover')} />
      </button>

      <div className="w-full flex items-center justify-center text-center mt-2 gap-2">
        {'isSelectionMode' in props && props.isSelectionMode && (
          <Checkbox
            name="person-selection-checkbox"
            checked={props.isSelected}
            onChange={() => props.onClickCheckbox(face.elementId)}
          />
        )}

        <div className="whitespace-nowrap overflow-hidden text-ellipsis">
          {face.name}
        </div>
      </div>

      {/* hidden admin data */}
      {adminMode && (
        <Popover
          placement="bottom"
          body={
            <div className="flex flex-col gap-1 text-xs leading-none">
              <DetailLine label="ID" value={face.elementId} />
            </div>
          }
        >
          <div className="text-center text-xs text-primary-900 leading-none underline whitespace-nowrap cursor-pointer">
            More Info
          </div>
        </Popover>
      )}
    </div>
  );
};

export default FaceCircle;
